/**
 * NeuralGraph — Premium 3D Neural Mesh (Three.js + Bloom + Drift)
 * Inspired by CodePen neural archive: bloom, drift, jitter, thick lines
 * Mouse-controlled rotation + slow idle auto-rotation
 * Auto color palette cycling every 30s
 * NO side panel, NO codepen branding
 */
import { Brain, RefreshCw, X, ZoomIn, ZoomOut } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { API_URL } from '../../config/api'
import subjectsData from '../../data/neural_subjects.json'

interface RawNode { id: string; label: string; type: string; metadata?: Record<string, any> }
interface RawEdge { source: string; target: string; relation_type?: string; weight?: number }
interface Subject {
  id: string; color: string; dead: string; synapse: string; name: string
  age: string; sex: string; diag: string
  memories: { status: string; text: string; wikiId?: string }[]
}
interface NeuralGraphProps {
  mode?: 'default' | 'archive' | 'twin'
  fullInterface?: boolean
}

// Color palettes that auto-cycle
const PALETTES = [
  { main: 0x6366f1, dead: 0x312e81, synapse: 0x818cf8 }, // indigo
  { main: 0xec4899, dead: 0x831843, synapse: 0xf472b6 }, // pink
  { main: 0x10b981, dead: 0x064e3b, synapse: 0x34d399 }, // emerald
  { main: 0xf59e0b, dead: 0x78350f, synapse: 0xfbbf24 }, // amber
  { main: 0x06b6d4, dead: 0x164e63, synapse: 0x22d3ee }, // cyan
  { main: 0xa855f7, dead: 0x581c87, synapse: 0xc084fc }, // purple
  { main: 0xef4444, dead: 0x7f1d1d, synapse: 0xf87171 }, // red
  { main: 0x38bdf8, dead: 0x0c4a6e, synapse: 0x7dd3fc }, // sky
]

export default function NeuralGraph({ mode = 'default', fullInterface = false }: NeuralGraphProps) {
  const [rawNodes, setRawNodes] = useState<RawNode[]>([])
  const [rawEdges, setRawEdges] = useState<RawEdge[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedMemory, setSelectedMemory] = useState<{
    text: string; status: string; id: number
    connections?: { label: string; type: string }[]
  } | null>(null)
  const [paletteIdx, setPaletteIdx] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sceneRef = useRef<{
    scene: THREE.Scene; camera: THREE.PerspectiveCamera
    renderer: THREE.WebGLRenderer; composer: EffectComposer
    nodeGroup: THREE.Group; atmosphereGroup: THREE.Group
    nodesArray: THREE.Mesh[]; linesArray: (THREE.Line | Line2)[]
    isMouseDown: boolean; isDragging: boolean
    dragStart: { x: number; y: number }
    palette: typeof PALETTES[0]
  } | null>(null)

  const activeSubject = (subjectsData as Subject[])[0] || null

  // ─── Data Loading ──────────────────────────────────────────────────────────
  const fetchGraph = useCallback(async () => {
    setLoading(true)
    if (mode === 'twin') {
      // Twin memory graph
      try {
        const token = localStorage.getItem('token')
        const headers: Record<string, string> = {}
        if (token) headers['Authorization'] = `Bearer ${token}`
        const res = await fetch(`${API_URL}/api/twin/habitat/neural-graph`, { headers })
        if (res.ok) {
          const d = await res.json()
          if (d.ok && d.nodes?.length > 0) {
            setRawNodes(d.nodes || [])
            setRawEdges(d.edges || [])
            setLoading(false)
            return
          }
        }
      } catch { /* fallback below */ }
      // Fallback to subject data
      if (activeSubject) {
        const nodes: RawNode[] = activeSubject.memories.map((m, i) => ({
          id: `mem_${i}`, label: m.text.substring(0, 25),
          type: m.status, metadata: { text: m.text, status: m.status }
        }))
        setRawNodes(nodes); setRawEdges([])
      }
      setLoading(false)
      return
    }
    if (mode === 'default') {
      try {
        const token = localStorage.getItem('token')
        const headers: Record<string, string> = {}
        if (token) headers['Authorization'] = `Bearer ${token}`
        const results = await Promise.allSettled([
          fetch(`${API_URL}/api/mycelium/graph`, { headers }),
          fetch(`${API_URL}/api/brain/graph`, { headers })
        ])
        const allN: RawNode[] = [], allE: RawEdge[] = [], seen = new Set<string>()
        for (const res of results) {
          if (res.status === 'fulfilled' && res.value.ok) {
            const d = await res.value.json()
            for (const n of (d.nodes || [])) { if (!seen.has(n.id)) { seen.add(n.id); allN.push(n) } }
            allE.push(...(d.edges || []))
          }
        }
        if (allN.length === 0 && activeSubject) {
          // Fallback to subject data
          const nodes: RawNode[] = activeSubject.memories.map((m, i) => ({
            id: `mem_${i}`, label: m.text.substring(0, 25),
            type: m.status, metadata: { text: m.text, status: m.status }
          }))
          const edges: RawEdge[] = []
          for (let i = 0; i < nodes.length - 1; i++) {
            if (Math.random() > 0.3) edges.push({ source: nodes[i].id, target: nodes[i + 1].id, relation_type: 'sequence' })
            if (i + 2 < nodes.length && Math.random() > 0.6) edges.push({ source: nodes[i].id, target: nodes[i + 2].id, relation_type: 'association' })
          }
          setRawNodes(nodes); setRawEdges(edges)
        } else {
          setRawNodes(allN); setRawEdges(allE)
        }
      } catch {
        // Fallback
        if (activeSubject) {
          const nodes: RawNode[] = activeSubject.memories.map((m, i) => ({
            id: `mem_${i}`, label: m.text.substring(0, 25),
            type: m.status, metadata: { text: m.text, status: m.status }
          }))
          setRawNodes(nodes); setRawEdges([])
        }
      }
    } else {
      if (activeSubject) {
        const nodes: RawNode[] = activeSubject.memories.map((m, i) => ({
          id: `mem_${i}`, label: m.text.substring(0, 25),
          type: m.status, metadata: { text: m.text, status: m.status }
        }))
        const edges: RawEdge[] = []
        for (let i = 0; i < nodes.length - 1; i++) {
          if (Math.random() > 0.3) edges.push({ source: nodes[i].id, target: nodes[i + 1].id, relation_type: 'sequence' })
          if (i + 2 < nodes.length && Math.random() > 0.6) edges.push({ source: nodes[i].id, target: nodes[i + 2].id, relation_type: 'association' })
        }
        setRawNodes(nodes); setRawEdges(edges)
      }
    }
    setLoading(false)
  }, [mode, activeSubject])

  useEffect(() => { void fetchGraph() }, [fetchGraph])

  // ─── Auto palette cycling ──────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      setPaletteIdx(prev => (prev + 1) % PALETTES.length)
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  // ─── Three.js Init ─────────────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current; if (!container) return
    const canvas = canvasRef.current; if (!canvas) return

    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0x0b0e14)
    scene.fog = new THREE.Fog(0x0b0e14, 80, 220)

    const camera = new THREE.PerspectiveCamera(50, container.offsetWidth / container.offsetHeight, 1, 1000)
    camera.position.z = 80

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
    renderer.setSize(container.offsetWidth, container.offsetHeight)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.toneMapping = THREE.ReinhardToneMapping

    // Post-processing: Bloom
    const composer = new EffectComposer(renderer)
    composer.addPass(new RenderPass(scene, camera))
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(container.offsetWidth, container.offsetHeight), 1.5, 0.4, 0.85
    )
    bloomPass.threshold = 0.3
    bloomPass.strength = 0.4
    bloomPass.radius = 0.2
    composer.addPass(bloomPass)

    const nodeGroup = new THREE.Group()
    const atmosphereGroup = new THREE.Group()
    scene.add(atmosphereGroup)
    scene.add(nodeGroup)

    scene.add(new THREE.AmbientLight(0xffffff, 0.6))
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.9)
    dirLight.position.set(20, 50, 30); scene.add(dirLight)

    // Atmosphere particles
    const pCount = 500
    const pGeo = new THREE.BufferGeometry()
    const pPos = new Float32Array(pCount * 3)
    for (let i = 0; i < pCount * 3; i++) pPos[i] = (Math.random() - 0.5) * 300
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3))
    atmosphereGroup.add(new THREE.Points(pGeo, new THREE.PointsMaterial({
      size: 1.0, color: 0x334155, transparent: true, opacity: 0.35
    })))

    sceneRef.current = {
      scene, camera, renderer, composer, nodeGroup, atmosphereGroup,
      nodesArray: [], linesArray: [],
      isMouseDown: false, isDragging: false, dragStart: { x: 0, y: 0 },
      palette: PALETTES[0]
    }

    let raf = 0
    const animate = () => {
      const s = sceneRef.current; if (!s) return
      const t = Date.now() * 0.001

      // Slow idle auto-rotation (stops when dragging)
      if (!s.isMouseDown) {
        s.nodeGroup.rotation.y += 0.0004
      }
      s.atmosphereGroup.rotation.y -= 0.0002

      // Node drift + jitter animation
      s.nodesArray.forEach(node => {
        const d = node.userData
        const mat = node.material as THREE.MeshPhongMaterial

        if (d.status === 'eroded') {
          // Jitter for eroded nodes
          node.position.x = d.basePos.x + Math.sin(t * 3 + d.phase) * 0.3
          node.position.y = d.basePos.y + Math.cos(t * 2.4 + d.phase) * 0.3
          if (Math.random() > 0.97) node.position.x += (Math.random() - 0.5) * 0.5
        } else {
          // Gentle drift for normal nodes
          node.position.y = d.basePos.y + Math.sin(t * 0.4 * d.speed + d.phase) * 1.2
          node.position.x = d.basePos.x + Math.cos(t * 0.3 * d.speed + d.phase * 0.7) * 0.6
        }

        // Pulse emissive
        mat.emissiveIntensity = node === activeNodeRef.current
          ? 1.0 + Math.sin(t * 5) * 0.4
          : 0.35 + Math.sin(t * 1.5 + d.phase) * 0.15
      })

      // Update line positions (follow drifting nodes)
      s.linesArray.forEach(line => {
        const { a, b } = line.userData as { a: THREE.Mesh; b: THREE.Mesh }
        if (line instanceof Line2) {
          const geo = line.geometry as LineGeometry
          geo.setPositions([a.position.x, a.position.y, a.position.z, b.position.x, b.position.y, b.position.z])
        } else {
          const geo = line.geometry as THREE.BufferGeometry
          const pos = geo.attributes.position as THREE.BufferAttribute
          pos.setXYZ(0, a.position.x, a.position.y, a.position.z)
          pos.setXYZ(1, b.position.x, b.position.y, b.position.z)
          pos.needsUpdate = true
        }
      })

      s.composer.render()
      raf = requestAnimationFrame(animate)
    }
    animate()

    const handleResize = () => {
      if (!container || !sceneRef.current) return
      const w = container.offsetWidth, h = container.offsetHeight
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)
      composer.setSize(w, h)
    }
    window.addEventListener('resize', handleResize)

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      camera.position.z = Math.max(20, Math.min(160, camera.position.z + e.deltaY * 0.05))
    }
    canvas.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', handleResize)
      canvas.removeEventListener('wheel', handleWheel)
      renderer.dispose()
    }
  }, [fullInterface, mode])

  const activeNodeRef = useRef<THREE.Mesh | null>(null)

  // ─── Build Scene (nodes + lines) ───────────────────────────────────────────
  useEffect(() => {
    if (loading || !rawNodes.length || !sceneRef.current) return
    const s = sceneRef.current
    const { nodeGroup } = s

    // Clear
    while (nodeGroup.children.length) nodeGroup.remove(nodeGroup.children[0])
    s.nodesArray = []
    s.linesArray = []

    const palette = PALETTES[paletteIdx]
    s.palette = palette

    const nodeGeo = new THREE.SphereGeometry(1.25, 32, 32)
    const nMap = new Map<string, THREE.Mesh>()

    rawNodes.forEach(n => {
      let color = palette.main
      let emissiveIntensity = 0.35
      if (n.type === 'latent' || n.type === 'concept') {
        color = 0xd1d5db
        emissiveIntensity = 0.15
      } else if (n.type === 'eroded' || n.type === 'skill') {
        color = palette.dead
        emissiveIntensity = 0.5
      } else {
        color = palette.main
        emissiveIntensity = 0.45
      }

      const mat = new THREE.MeshPhongMaterial({
        color, emissive: color, emissiveIntensity,
        specular: 0x444444, shininess: 60
      })
      const mesh = new THREE.Mesh(nodeGeo, mat)
      mesh.position.set(
        (Math.random() - 0.5) * 80,
        (Math.random() - 0.5) * 80,
        (Math.random() - 0.5) * 80
      )
      mesh.userData = {
        id: n.id, raw: n,
        basePos: mesh.position.clone(),
        phase: Math.random() * Math.PI * 2,
        speed: 0.5 + Math.random() * 0.5,
        status: n.type
      }
      nodeGroup.add(mesh)
      s.nodesArray.push(mesh)
      nMap.set(n.id, mesh)
    })

    // Lines (thick Line2 — like CodePen)
    const container = containerRef.current
    const resolution = container ? new THREE.Vector2(container.offsetWidth, container.offsetHeight) : new THREE.Vector2(800, 600)

    // Edges from data — thick synapse lines
    rawEdges.forEach(e => {
      const a = nMap.get(e.source), b = nMap.get(e.target)
      if (a && b) {
        const lineGeo = new LineGeometry()
        lineGeo.setPositions([a.position.x, a.position.y, a.position.z, b.position.x, b.position.y, b.position.z])
        const lineMat = new LineMaterial({
          color: palette.synapse, linewidth: 2,
          transparent: true, opacity: 0.8,
          resolution
        })
        const line = new Line2(lineGeo, lineMat)
        line.userData = { a, b, type: e.relation_type }
        nodeGroup.add(line)
        s.linesArray.push(line)
      }
    })

    // Proximity lines — thinner
    for (let i = 0; i < s.nodesArray.length; i++) {
      for (let j = i + 1; j < s.nodesArray.length; j++) {
        if (s.nodesArray[i].position.distanceTo(s.nodesArray[j].position) < 15) {
          const lineGeo = new LineGeometry()
          lineGeo.setPositions([
            s.nodesArray[i].position.x, s.nodesArray[i].position.y, s.nodesArray[i].position.z,
            s.nodesArray[j].position.x, s.nodesArray[j].position.y, s.nodesArray[j].position.z
          ])
          const lineMat = new LineMaterial({
            color: palette.synapse, linewidth: 1,
            transparent: true, opacity: 0.25,
            resolution
          })
          const line = new Line2(lineGeo, lineMat)
          line.userData = { a: s.nodesArray[i], b: s.nodesArray[j], type: 'proximity' }
          nodeGroup.add(line)
          s.linesArray.push(line)
        }
      }
    }
  }, [rawNodes, rawEdges, loading, paletteIdx, mode])

  // ─── Color transition on palette change ────────────────────────────────────
  useEffect(() => {
    if (!sceneRef.current) return
    const s = sceneRef.current
    const palette = PALETTES[paletteIdx]
    s.palette = palette

    // Smoothly transition node colors
    s.nodesArray.forEach(node => {
      const mat = node.material as THREE.MeshPhongMaterial
      const status = node.userData.status
      let targetColor = palette.main
      if (status === 'latent' || status === 'concept') targetColor = 0xd1d5db
      else if (status === 'eroded' || status === 'skill') targetColor = palette.dead

      mat.color.setHex(targetColor)
      mat.emissive.setHex(targetColor)
    })

    // Transition line colors
    s.linesArray.forEach(line => {
      if (line instanceof Line2) {
        const mat = line.material as LineMaterial
        mat.color.setHex(palette.synapse)
      } else {
        const mat = line.material as THREE.LineBasicMaterial
        mat.color.setHex(palette.synapse)
      }
    })
  }, [paletteIdx])

  // ─── Mouse Interaction ─────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    const s = sceneRef.current; if (!s) return
    s.isMouseDown = true; s.isDragging = false
    s.dragStart = { x: e.clientX, y: e.clientY }
  }

  const onMouseMove = (e: React.MouseEvent) => {
    const s = sceneRef.current; if (!s) return
    const rect = canvasRef.current?.getBoundingClientRect(); if (!rect) return

    // Hover detection
    const mouse = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, s.camera)
    const hits = raycaster.intersectObjects(s.nodesArray)

    // Scale nodes on hover
    s.nodesArray.forEach(n => {
      const target = n === activeNodeRef.current ? 1.8 : (hits.length > 0 && hits[0].object === n ? 1.6 : 1.0)
      n.scale.lerp(new THREE.Vector3(target, target, target), 0.15)
    })

    if (hits.length > 0) {
      if (canvasRef.current) canvasRef.current.style.cursor = 'pointer'
    } else {
      if (canvasRef.current) canvasRef.current.style.cursor = s.isMouseDown ? 'grabbing' : 'grab'
    }

    // Drag rotation
    if (s.isMouseDown) {
      const dx = e.clientX - s.dragStart.x, dy = e.clientY - s.dragStart.y
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        s.isDragging = true
        s.nodeGroup.rotation.y += dx * 0.005
        s.nodeGroup.rotation.x += dy * 0.005
        s.dragStart = { x: e.clientX, y: e.clientY }
      }
    }
  }

  const onMouseUp = (e: React.MouseEvent) => {
    const s = sceneRef.current; if (!s) return

    if (!s.isDragging) {
      // Click — select node
      const rect = canvasRef.current?.getBoundingClientRect(); if (!rect) return
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      )
      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(mouse, s.camera)
      const hits = raycaster.intersectObjects(s.nodesArray)

      if (hits.length > 0) {
        const hit = hits[0].object as THREE.Mesh
        activeNodeRef.current = hit
        const nodeData = hit.userData.raw as RawNode

        // Find connections
        const connections = rawEdges
          .filter(e => e.source === nodeData.id || e.target === nodeData.id)
          .map(e => {
            const otherId = e.source === nodeData.id ? e.target : e.source
            const otherNode = rawNodes.find(n => n.id === otherId)
            return { label: otherNode?.label || otherId, type: e.relation_type || 'link' }
          })

        setSelectedMemory({
          text: nodeData.metadata?.text || nodeData.label,
          status: nodeData.metadata?.status || nodeData.type,
          id: Math.floor(Math.random() * 9999),
          connections
        })
      } else {
        activeNodeRef.current = null
        setSelectedMemory(null)
      }
    }
    s.isMouseDown = false; s.isDragging = false
  }

  const onMouseLeave = () => {
    const s = sceneRef.current; if (!s) return
    s.isMouseDown = false; s.isDragging = false
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{
      position: 'relative', height: '100%', width: '100%',
      background: '#0b0e14', color: '#fff', overflow: 'hidden',
      borderRadius: fullInterface ? 0 : 16
    }}>
      {/* Controls */}
      {fullInterface && (
        <>
          <div style={{ position: 'absolute', top: 16, left: 16, zIndex: 100, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)', padding: '8px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Brain size={16} color={`#${PALETTES[paletteIdx].main.toString(16).padStart(6, '0')}`} />
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--border-default)' }}>
                {mode === 'twin' ? 'Digital Twin' : 'Neural Graph'}
              </span>
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.03)', padding: '4px 8px', borderRadius: 6 }}>
              {rawNodes.length} nodes · {rawEdges.length} edges
            </div>
          </div>

          <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 100, display: 'flex', gap: 4 }}>
            <button onClick={() => void fetchGraph()} style={ctrlBtn}><RefreshCw size={14} /></button>
            <button onClick={() => { if (sceneRef.current) sceneRef.current.camera.position.z -= 10 }} style={ctrlBtn}><ZoomIn size={14} /></button>
            <button onClick={() => { if (sceneRef.current) sceneRef.current.camera.position.z += 10 }} style={ctrlBtn}><ZoomOut size={14} /></button>
            <button onClick={() => setPaletteIdx(p => (p + 1) % PALETTES.length)} style={{ ...ctrlBtn, fontSize: 10 }}>🎨</button>
          </div>
        </>
      )}

      <canvas
        ref={canvasRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        style={{ width: '100%', height: '100%', display: 'block', cursor: 'grab' }}
      />

      {/* Bottom memory panel (no side panel) */}
      {selectedMemory && (
        <div style={{
          position: 'absolute', bottom: 16, left: 16, right: 16, maxWidth: 520,
          background: 'rgba(15, 23, 42, 0.92)', backdropFilter: 'blur(24px)',
          border: `1px solid rgba(${(PALETTES[paletteIdx].main >> 16) & 0xff}, ${(PALETTES[paletteIdx].main >> 8) & 0xff}, ${PALETTES[paletteIdx].main & 0xff}, 0.4)`,
          borderRadius: 16, padding: '14px 18px', zIndex: 200,
          boxShadow: '0 20px 60px rgba(0,0,0,0.7)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: `#${PALETTES[paletteIdx].synapse.toString(16).padStart(6, '0')}`, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                {selectedMemory.status} · MEM_{selectedMemory.id}
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--border-default)', lineHeight: 1.5 }}>
                {selectedMemory.status === 'eroded'
                  ? selectedMemory.text.split('').map(c => Math.random() > 0.7 ? '░' : c).join('')
                  : selectedMemory.text
                }
              </div>
            </div>
            <button onClick={() => { setSelectedMemory(null); activeNodeRef.current = null }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4, marginLeft: 8 }}>
              <X size={14} />
            </button>
          </div>

          {selectedMemory.connections && selectedMemory.connections.length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {selectedMemory.connections.slice(0, 6).map((c, i) => (
                <span key={i} style={{
                  fontSize: 9, padding: '2px 7px', borderRadius: 5,
                  background: `rgba(${(PALETTES[paletteIdx].synapse >> 16) & 0xff}, ${(PALETTES[paletteIdx].synapse >> 8) & 0xff}, ${PALETTES[paletteIdx].synapse & 0xff}, 0.15)`,
                  color: `#${PALETTES[paletteIdx].synapse.toString(16).padStart(6, '0')}`,
                  border: `1px solid rgba(${(PALETTES[paletteIdx].synapse >> 16) & 0xff}, ${(PALETTES[paletteIdx].synapse >> 8) & 0xff}, ${PALETTES[paletteIdx].synapse & 0xff}, 0.3)`
                }}>
                  {c.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hint */}
      {fullInterface && !selectedMemory && (
        <div style={{ position: 'absolute', bottom: 12, left: 16, fontSize: 9, color: 'var(--text-secondary)', fontWeight: 600 }}>
          DRAG rotate · CLICK inspect · SCROLL zoom · 🎨 palette
        </div>
      )}
    </div>
  )
}

const ctrlBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
  color: 'var(--text-tertiary)', cursor: 'pointer', width: 30, height: 30, borderRadius: 8,
  display: 'flex', alignItems: 'center', justifyContent: 'center'
}
